import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SkuCardData } from "@/components/chat/SkuCard";
import { deriveBrowseMeta } from "@/lib/deriveBrowseMeta";

/**
 * Sprint 2.0e-bias, popular SKUs hook for /browse.
 *
 * Calls the get-popular-skus edge function (anon-callable) and
 * hydrates the returned sku_ids against vendor_products so /browse
 * can render a "Popular picks" row above the main grid.
 *
 * The hook is two queries chained:
 *   1. usePopularSkuIds → just the sku_ids + counts from the
 *      edge function. Cached aggressively (10 min staleTime, pick
 *      counts shift slowly, and the edge function caches its own
 *      response at the CDN for 5 min).
 *   2. usePopularSkuCards → the hydrated SkuCardData[] ready to
 *      render. Depends on (1); enabled only after (1) resolves
 *      with at least one sku_id.
 *
 * Both queries fail silently, pre-launch state is "no popular SKUs
 * yet" (returns empty array) and /browse degrades cleanly (the row
 * just doesn't render).
 */

export interface PopularSkuId {
  sku_id: string;
  pick_count: number;
}

/**
 * Sprint 2.0e-bias-v2, popular-pick card with the extra fields
 * /browse needs for client-side filter intersection (dietary_tags
 * isn't on the base SkuCardData since the chat surface doesn't
 * filter on it). Keeps SkuCardData clean, anywhere else still
 * gets the slim shape, only /browse pays for the richer payload.
 */
export interface PopularPickCard extends SkuCardData {
  dietary_tags: string[] | null;
}

export function usePopularSkuIds() {
  return useQuery({
    queryKey: ["popular-skus", "ids"],
    staleTime: 10 * 60 * 1000, // 10 min, pick counts move slowly
    retry: 1,                   // single retry; edge function may be cold
    queryFn: async (): Promise<PopularSkuId[]> => {
      try {
        const { data, error } = await supabase.functions.invoke("get-popular-skus", {
          body: {},               // GET endpoint; supabase-js still sends a body header
          method: "GET",
        });
        if (error) {
          // Function not deployed (404) or service down, graceful
          // empty so /browse keeps working.
          return [];
        }
        if (!data?.ok || !Array.isArray(data.popular)) return [];
        return data.popular as PopularSkuId[];
      } catch {
        return [];
      }
    },
  });
}

/**
 * Hydrated SkuCardData[] for the popular SKUs. Fetches vendor_products
 * for the sku_ids returned by usePopularSkuIds. Preserves the pick-
 * count order in the output (server-side .in() doesn't preserve
 * order, so we re-sort client-side after fetch). Drops SKUs that
 * have been deleted between recommendation time and now.
 */
export function usePopularSkuCards() {
  const idsQuery = usePopularSkuIds();
  const ids = idsQuery.data ?? [];
  const idList = ids.map((p) => p.sku_id);
  return useQuery({
    queryKey: ["popular-skus", "cards", idList.join(",")],
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<PopularPickCard[]> => {
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("vendor_products")
        .select(
          "id, name, description, price, image_url, slug, vendor_category, lead_time_days, is_voucher, dietary_tags, vendor_partners(vendor_name)",
        )
        .in("id", idList)
        .is("deleted_at", null)
        // MS285: was missing — a SKU that was popular last week but is
        // now vendor-paused (is_available=false) would still surface
        // on the popular-picks row. Mirrors the MS283 fix in the chat
        // search RPC + Browse.tsx + chat-concierge fallback so every
        // discovery surface honours the same flag consistently.
        .eq("is_available", true);
      if (error || !data) return [];
      type Row = {
        id: string;
        name: string;
        description: string | null;
        price: number | string | null;
        image_url: string | null;
        slug: string | null;
        vendor_category: string;
        lead_time_days: number | null;
        is_voucher: boolean | null;
        dietary_tags: string[] | null;
        vendor_partners: { vendor_name: string } | { vendor_name: string }[] | null;
      };
      const byId = new Map<string, Row>();
      for (const r of data as Row[]) byId.set(r.id, r);
      // Re-sort by the original pick-count order (which the .in()
      // shuffled). Skip SKUs that no longer exist (catalogue churn
      // between recommendation and now).
      const cards: PopularPickCard[] = [];
      for (const p of ids) {
        const row = byId.get(p.sku_id);
        if (!row) continue;
        const vendor = Array.isArray(row.vendor_partners)
          ? row.vendor_partners[0]
          : row.vendor_partners;
        cards.push({
          sku_id: row.id,
          name: row.name,
          vendor_name: vendor?.vendor_name ?? "Lembra",
          vendor_category: row.vendor_category,
          price_aud: Number(row.price ?? 0),
          image_url: row.image_url,
          slug: row.slug ?? row.id,
          description: row.description,
          is_voucher: Boolean(row.is_voucher),
          // MS312: propagate raw lead_time_days for OrderConfirmSheet
          // validation. highlight_fields stays the human-readable surface.
          lead_time_days: row.lead_time_days,
          // Sprint 2.0e-bias-v2, preserved for client-side filter
          // intersection in /browse. Not used by SkuCard render.
          dietary_tags: row.dietary_tags,
          highlight_fields: deriveBrowseMeta({
            vendor_category: row.vendor_category,
            lead_time_days: row.lead_time_days,
            is_voucher: row.is_voucher ?? false,
            dietary_tags: row.dietary_tags,
            description: row.description,
          }),
        });
      }
      return cards;
    },
  });
}
